import { useCallback, useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';

import { mapProductPageVariant } from './utils';

export function useDataLayerProduct({
  baseEcommerce,
  DEBUG,
  userDataEvent,
  userProperties,
}) {
  const productHandleRef = useRef(null);
  const productBundleRef = useRef(null);

  const [viewedProductVariant, setViewedProductVariant] = useState(null);
  const [viewedProductBundleVariants, setViewedProductBundleVariants] =
    useState(null);

  const viewProductEvent = useCallback(
    ({ ecommerce, variant, userProperties: _userProperties }) => {
      if (!variant) return;
      const previousPath = sessionStorage.getItem('PREVIOUS_PATH');
      const list = previousPath?.startsWith('/collections') ? previousPath : '';
      const event = {
        event: 'dl_view_item',
        event_id: nanoid(),
        event_time: new Date().toISOString(),
        user_properties: _userProperties,
        ecommerce: {
          ...ecommerce,
          detail: {
            actionField: { list, action: 'detail' },
            products: [variant].map(mapProductPageVariant(list)),
          },
        },
      };

      window.dispatchEvent(
        new CustomEvent(event.event, {
          detail: event,
        })
      );
      if (DEBUG) console.log(`DataLayer:dispatch:${event.event}`, event);
    },
    []
  );

  const viewProductBundleEvent = useCallback(
    ({ ecommerce, variants, userProperties: _userProperties }) => {
      if (!variants?.length) return;
      const previousPath = sessionStorage.getItem('PREVIOUS_PATH');
      const list =
        previousPath?.startsWith('/collections') ||
        (previousPath?.startsWith('/pages') && previousPath?.includes('kits'))
          ? previousPath
          : '';
      const event = {
        event: 'dl_view_item',
        user_properties: _userProperties,
        ecommerce: {
          ...ecommerce,
          detail: {
            actionField: { list, action: 'detail' },
            products: variants.map(mapProductPageVariant(list)),
          },
        },
      };

      window.dispatchEvent(
        new CustomEvent(event.event, {
          detail: event,
        })
      );
      if (DEBUG) console.log(`DataLayer:dispatch:${event.event}`, event);
    },
    []
  );

  // Subscribe to PubSub topic for 'dl_view_item' event
  useEffect(() => {
    const viewProduct = PubSub.subscribe(
      'VIEW_PRODUCT_PAGE',
      async (event, variant) => {
        setViewedProductVariant(variant);
      }
    );
    const viewProductBundle = PubSub.subscribe(
      'VIEW_PRODUCT_BUNDLE_PAGE',
      async (event, variants) => {
        setViewedProductBundleVariants(variants);
      }
    );
    return () => {
      if (viewProduct) {
        PubSub.unsubscribe(viewProduct);
      }
      if (viewProductBundle) {
        PubSub.unsubscribe(viewProductBundle);
      }
    };
  }, []);

  // Trigger 'dl_user_data' and 'dl_view_item' events on product change after base data is ready
  useEffect(() => {
    const pageHandle = window.location.pathname.split('/').pop();
    if (
      !baseEcommerce ||
      !userProperties ||
      !viewedProductVariant ||
      productHandleRef.current === pageHandle
    )
      return;
    userDataEvent({ ecommerce: baseEcommerce, userProperties });
    viewProductEvent({
      ecommerce: baseEcommerce,
      variant: viewedProductVariant,
      userProperties,
    });
    productHandleRef.current = pageHandle;
  }, [!!baseEcommerce, viewedProductVariant?.product?.id, !!userProperties]);

  // Trigger 'dl_user_data' and 'dl_view_item' events on viewedProductVariant change after base data is ready
  useEffect(() => {
    const productBundlePathname = window.location.pathname;
    if (
      !baseEcommerce ||
      !userProperties ||
      !viewedProductBundleVariants?.length ||
      productBundleRef.current === productBundlePathname
    )
      return;
    userDataEvent({ ecommerce: baseEcommerce, userProperties });
    viewProductBundleEvent({
      ecommerce: baseEcommerce,
      variants: viewedProductBundleVariants,
      userProperties,
    });
    productBundleRef.current = productBundlePathname;
  }, [!!baseEcommerce, viewedProductBundleVariants, !!userProperties]);
}
